import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import isEmpty from 'ramda/src/isEmpty';
import curry from 'ramda/src/curry';
import snakecaseKeys from 'snakecase-keys';

import projectApi from '/api/project';
import offerApi from '/api/offer';
import {
    JobTemplate,
    JobDestinations,
    QuestionType,
    Answer,
    Answers,
    JobTemplateType,
} from '/types/jobTemplateTypes';
import {
    PartialTemplateJobLineItem,
    LineItemType,
    AnyLineItem,
} from '/types/lineItemTypes';
import Draft, { ProjectDraft, OfferDraft } from '/types/draftType';
import {
    areSameArrays,
    pathNameIs,
    goTo,
    parseQueryParams,
    isServer,
} from '/lib/helpers';
import useUserIs from '/hooks/useUserIs';
import calculationApi from '/api/calculations';
import orderApi from '/api/order';
import { useOrderState } from '/store/orderStore';
import { useUserState } from '/store/userStore';
import { error } from '/components/ui/toast';
import { trackAddToCart } from '/lib/ga-data-layer';
import usePostCodeState from '/hooks/usePostCodeState';
import useIsJobSupported from '/hooks/useIsJobSupported';
import ResourceType from '/types/resourceType';

interface Props {
    job: JobTemplate;
    jobDestination: JobDestinations;
    draft?: Draft;
}

let timeout: ReturnType<typeof setTimeout>;

export default function useJobConfiguration({
    job,
    jobDestination,
    draft,
}: Props) {
    const router = useRouter();

    const cart = useOrderState((state) => state.cart);
    const setOrderCart = useOrderState((state) => state.setOrderCart);
    const clearOrderCart = useOrderState((state) => state.clearOrderCart);

    const user = useUserState((state) => state.user);
    const userIs = useUserIs();
    const pageIs = pathNameIs(router.pathname);

    const [totals, setTotals] = useState<JobTemplate['totals']>(job.totals);
    const [questions, setQuestions] = useState<
        JobTemplate['jobTypeVersion']['questions']
    >(job.jobTypeVersion.questions);
    const [answers, setAnswers] = useState<Answers>(job.answers);
    const [materials, setMaterials] = useState<JobTemplate['materials']>(
        job.materials
    );
    const [materialsEdited, setMaterialsEdited] = useState<boolean>(
        job.materialsEdited
    );
    const [editedMaterialList, setEditedMaterialList] = useState<
        JobTemplate['editedMaterialList']
    >(job.editedMaterialList);
    const [importantNote, setImportantNote] = useState<string>(
        job.jobTypeVersion.info.importantNote || ''
    );
    const [images, setImages] = useState<JobTemplate['images']>(job.images);
    const [missingImagesModalVisible, setMissingImagesModalVisible] =
        useState<boolean>(false);
    const [sellerConfirmationModalVisible, setSellerConfirmationModalVisible] =
        useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [productSetupPerformed, setProductSetupPerformed] =
        useState<boolean>(false);

    const prevState = useRef<{
        answers: Answers;
        materialsEdited: boolean;
        editedMaterialList: JobTemplate['editedMaterialList'];
    }>({ answers, materialsEdited, editedMaterialList });

    const revisionRequired = useRef<boolean>(false);

    const isJobFromCart = !!router.query.cartJobRef;

    const isJobFromDraft = !!draft?.lineItems?.find(
        (item: any) => item?.data?.jobTypeId === job.jobTypeId
    );

    const isJobSupported = useIsJobSupported({
        resourceType: ResourceType.CART,
    });

    const { postCodeSupportedJobTypes } = usePostCodeState();

    const productsFromQuery = isServer
        ? undefined
        : parseQueryParams(window.location.search).prod;

    useEffect(() => {
        setTotals(job.totals);
        setQuestions(job.jobTypeVersion.questions);
        setAnswers(job.answers);
        setMaterials(job.materials);
        setMaterialsEdited(job.materialsEdited);
        setEditedMaterialList(job.editedMaterialList);
        setImportantNote(job.jobTypeVersion.info.importantNote || '');
        setImages(job.images);
    }, [job.jobTypeId]);

    useEffect(() => {
        const { changed, added, removed } = answerDifferences(
            prevState.current.answers,
            answers
        );
        const answersChanged = !isEmpty([...changed, ...added, ...removed]);
        const onlyFileUploadChanged = onlyFileUploadChange([
            ...changed,
            ...added,
            ...removed,
        ]);

        if (answersChanged && !onlyFileUploadChanged) {
            if (materialsEdited && editedMaterialList) {
                // if materials were edited and answer is changing we
                // get rid of edited material list as one based on answers
                // will be new source of truth.
                // Since we are changing state that will trigger recalculation
                // we don't need to call recalculate here.
                setMaterialsEdited(false);
                setEditedMaterialList(null);
            } else {
                recalculateWithDebounce();
            }
        }
        prevState.current.answers = answers;
    }, [answers]);

    useEffect(() => {
        if (
            editedMaterialList &&
            prevState.current.editedMaterialList &&
            !areSameArrays(
                editedMaterialList,
                prevState.current.editedMaterialList
            )
        ) {
            recalculateWithDebounce();
        }
        prevState.current.editedMaterialList = editedMaterialList;
    }, [editedMaterialList]);

    useEffect(() => {
        if (materialsEdited !== prevState.current.materialsEdited) {
            recalculateWithDebounce();
        }
        prevState.current.materialsEdited = materialsEdited;
    }, [materialsEdited]);

    useEffect(() => {
        if (productsFromQuery) {
            recalculate();
        }
    }, []);

    const recalculateWithDebounce = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            recalculate();
        }, 200);
    };

    const answersHaveSameIds = curry(
        (answerA: Answer, answerB: Answer) =>
            answerA.questionId === answerB.questionId &&
            JSON.stringify(answerA.answerId) ===
                JSON.stringify(answerB.answerId)
    );

    const answerDifferences = (prevAnswers: Answers, newAnswers: Answers) => {
        return {
            added:
                prevAnswers.length < newAnswers.length
                    ? newAnswers.filter((newAnswer) => {
                          return !prevAnswers.find(
                              answersHaveSameIds(newAnswer)
                          );
                      })
                    : [],
            removed:
                prevAnswers.length > newAnswers.length
                    ? prevAnswers.filter((prevAnswer) => {
                          return !newAnswers.find(
                              answersHaveSameIds(prevAnswer)
                          );
                      })
                    : [],
            changed: newAnswers.filter((newAnswer) => {
                const prevAnswer = prevAnswers.find(
                    answersHaveSameIds(newAnswer)
                );
                return JSON.stringify(prevAnswer) !== JSON.stringify(newAnswer);
            }),
        };
    };

    const onlyFileUploadChange = (answerChanges: Answer[]) => {
        return (
            answerChanges.length === 1 &&
            answerChanges[0].questionType === QuestionType.FILE_UPLOAD
        );
    };

    const getCartUuid = async (cartExpired: boolean = false) => {
        if (cart && !cartExpired) return cart.uuid;

        const userData =
            !user || userIs.salesPartnerSeller
                ? {}
                : {
                      userUuid: user.uuid,
                      prefilledUserData: {
                          name: user.name,
                          email: user.email,
                          phoneNumber: user.phoneNumber,
                      },
                  };

        const createCartResp = await orderApi.cart.create({
            source: userIs.salesPartnerSeller ? 'SALES_PARTNER' : 'SPOTON',
            ...userData,
        });

        if ('error' in createCartResp) {
            error(createCartResp.error);
        }

        return createCartResp.data.uuid;
    };

    const missingImages = () => {
        // if job is edited by electrician on a PROJECT draft images don't
        // need to be provided
        if (
            userIs.electricianOrContractor &&
            jobDestination === JobDestinations.PROJECT
        ) {
            return false;
        }

        return answers.some(
            (a: Answer) =>
                a.questionType === QuestionType.FILE_UPLOAD &&
                isEmpty(a.answerValue)
        );
    };

    const recalculate = async () => {
        const draftProjectId =
            draft && jobDestination === 'project' && 'project' in draft
                ? draft.project.id
                : undefined;

        const draftOfferId =
            draft &&
            jobDestination === 'offer' &&
            'project' in draft &&
            'offer' in draft.project
                ? draft.project.offer.id
                : undefined;

        const lineItem = configurationToLineItem();

        const resp = await calculationApi.getForLineItem({
            lineItem: lineItem,
            projectId: draftProjectId,
            offerId: draftOfferId,
        });

        if ('error' in resp) {
            error(resp.error);
            return;
        }

        if (productsFromQuery) {
            setProductSetupPerformed(true);
        }

        setTotals(resp.data.totals);
        setQuestions(resp.data.jobTypeVersion.questions);
        setAnswers(resp.data.answers);
        setMaterials(resp.data.materials);

        const productsFromAnswers = resp.data.answers
            .filter((a) => a.productId)
            .map((a) => a.productId)
            .flatMap((id) => id)
            .map((id) => String(id));

        if (productsFromAnswers.length > 0 && pageIs.configurator) {
            router.replace(
                {
                    query: { prod: productsFromAnswers },
                    pathname: router.asPath.split('?')[0],
                },
                undefined,
                {
                    shallow: true,
                }
            );
        }
    };

    const submitConfiguration = async () => {
        if (submitting) return;

        if (!userIs.salesPartnerSeller && missingImages()) {
            setMissingImagesModalVisible(true);
            return;
        }

        if (userIs.salesPartnerSeller && !sellerConfirmationModalVisible) {
            setSellerConfirmationModalVisible(true);
            return;
        }

        // if revisionRequired = false (as stated by seller user)
        // but image is missing we consider revisionRequired to be true anyway
        if (
            userIs.salesPartnerSeller &&
            revisionRequired.current === false &&
            missingImages()
        ) {
            revisionRequired.current = true;
        }

        setSubmitting(true);

        if (jobDestination === JobDestinations.CART) {
            await addToCart();
            setSubmitting(false);
            return;
        }

        await addToResource();
        setSubmitting(false);
    };

    const addToCart = async (cartExpired: boolean = false): Promise<any> => {
        const cartUuid = await getCartUuid(cartExpired);

        if (!cartUuid) return error('Could not a get cart UUID');

        const lineItem = configurationToLineItem();

        let lineItemQueryReference = router.query.cartJobRef;

        if (isJobFromCart) {
            // update existing cart job
            const lineItemId = cart?.lineItems?.find(
                (item) =>
                    item.type === 'JOB' &&
                    item.reference === lineItemQueryReference
            )?.id;

            const updateCartLineItemResp = await orderApi.cart.lineItem.update(
                cartUuid,
                lineItemId,
                lineItem
            );
            if ('error' in updateCartLineItemResp) {
                return error(updateCartLineItemResp.error);
            }
        } else {
            // add new job to the cart
            const createCartLineItemResp = await orderApi.cart.lineItem.create(
                cartUuid,
                lineItem
            );

            if (
                createCartLineItemResp?.errorCode === 'CART_EXPIRED' &&
                !cartExpired
            ) {
                clearOrderCart();
                return addToCart(true);
            }

            if ('error' in createCartLineItemResp) {
                return error(createCartLineItemResp.error);
            }
            lineItemQueryReference = createCartLineItemResp.reference;
        }

        // get updated cart
        const cartResp = await orderApi.cart.get(cartUuid);
        if ('error' in cartResp) return error(cartResp.error);

        // set cart in store
        setOrderCart(cartResp.data);
        trackAddToCart(job, totals.totalCost);

        // if adding heat pump job and no coverage for electrician jobs inform
        // about el. jobs becoming available trough query param
        const electricianJobsEnabled =
            job.typeKey === 'HEAT_PUMP' &&
            isJobSupported(job.typeKey) &&
            !postCodeSupportedJobTypes.includes(JobTemplateType.ELECTRICIAN)
                ? '&electricianJobsEnabled=1'
                : '';

        goTo(
            `/bestilling/sparpenger?ref=${lineItemQueryReference}${electricianJobsEnabled}`
        );
    };

    const configurationToLineItem = (
        options: { snakeCaseData: boolean } = { snakeCaseData: false }
    ): PartialTemplateJobLineItem => {
        const productIds = (
            Array.isArray(productsFromQuery)
                ? productsFromQuery
                : [productsFromQuery]
        )
            .map(Number)
            .filter((x) => !isNaN(x));

        const data = {
            jobTypeId: job.jobTypeId,
            jobTypeVersionId: job.jobTypeVersion.id,
            answers: answers,
            materials: materials,
            materialsEdited: materialsEdited,
            editedMaterialList: editedMaterialList,
            images: images,
            importantInformation: importantNote,
            totals: totals,
            revisionRequired: userIs.salesPartnerSeller
                ? revisionRequired.current
                : false,
            products:
                !productSetupPerformed && productIds.length > 0
                    ? productIds
                    : undefined,
        };

        return {
            type: LineItemType.JOB,
            data: options.snakeCaseData
                ? snakecaseKeys(data, { deep: false })
                : data,
        };
    };

    const areEditedMaterialsInvalid = () => {
        return (editedMaterialList || []).find((m) => {
            !m.quantity || (m.price && !Number(m.price.spotonPrice));
        });
    };

    const getEditedLineItem = () => {
        const lineItemQueryReference = router.query.job_reference;
        return (draft?.lineItems || []).find(
            (item: AnyLineItem) =>
                item.reference && item.reference === lineItemQueryReference
        );
    };

    const getResourceApi = () => {
        if (jobDestination === JobDestinations.PROJECT) return projectApi;
        if (jobDestination === JobDestinations.OFFER) return offerApi;
    };

    const getResourceId = (): number => {
        if (jobDestination === JobDestinations.PROJECT) {
            return (draft as ProjectDraft).project.id;
        }
        if (jobDestination === JobDestinations.OFFER) {
            return (draft as OfferDraft).offer.id;
        }

        return 0;
    };

    const addToResource = async () => {
        const api = getResourceApi();
        const resourceId = getResourceId();
        const resourceQueryReference = router.query.reference;
        const editedLineItem = getEditedLineItem();

        if (materialsEdited && editedMaterialList) {
            if (areEditedMaterialsInvalid()) {
                setSubmitting(false);
                error(
                    'Some of the materials have price and/or quantity set to "0". Please remove materials that are not needed instead of setting their price/quantity to "0".'
                );
                return;
            }
        }

        if (api === undefined) {
            error('Cound not match API endpoint.');
            return;
        }

        if (!editedLineItem) {
            // create line item
            const createLineItemResp = await api.draft.createLineItem(
                resourceId,
                configurationToLineItem({ snakeCaseData: true })
            );
            if ('error' in createLineItemResp) {
                error(createLineItemResp.error);
                return;
            }
        } else {
            // update line item
            const updateLineItemResp = await api.draft.updateLineItem(
                resourceId,
                editedLineItem.id,
                configurationToLineItem({ snakeCaseData: true })
            );
            if ('error' in updateLineItemResp) {
                error(updateLineItemResp.error);
                return;
            }
        }

        if (jobDestination === JobDestinations.OFFER) {
            return goTo(`/prosjekt/${resourceQueryReference}/tilbud/rediger`);
        }

        if (jobDestination === JobDestinations.PROJECT) {
            return goTo(`/prosjekt/${resourceQueryReference}/rediger`);
        }
    };

    return {
        isJobFromCart,
        isJobFromDraft,
        missingImagesModalVisible,
        sellerConfirmationModalVisible,
        submitConfiguration,
        jobData: {
            info: job.jobTypeVersion.info,
            questions,
            answers,
            totals,
            materials,
            materialsEdited,
            editedMaterialList,
            importantNote,
            images,
        },
        setters: {
            setAnswers,
            setMaterials,
            setMaterialsEdited,
            setEditedMaterialList,
            setImportantNote,
            setImages,
            setMissingImagesModalVisible,
            setSellerConfirmationModalVisible,
            setRevisionRequired: (val: boolean) =>
                (revisionRequired.current = val),
        },
    };
}
